/**
 * Canonical list of active anomaly rule codes with user-facing labels and info text.
 * Used by AnomalyList, AnomalyRuleConfig, AnomalyDashboard, and anywhere
 * a rule code → label / description mapping is needed.
 */
const ANOMALY_RULE_CODES = [
  {
    value: "SPIKE_RULE",
    label: "Unusual Increase",
    info: "Checks whether the current value rises sharply compared with the previous period. Helps identify unexpected jumps or data entry mistakes.",
  },
  {
    value: "DROP_RULE",
    label: "Unusual Decrease",
    info: "Checks whether the current value falls sharply compared with the previous period. Helps identify missing data or incorrect entries.",
  },
  {
    value: "ZERO_AFTER_NON_ZERO_RULE",
    label: "Sudden Zero",
    info: "Checks whether a metric becomes zero after it already had real values in earlier periods. Helps catch accidental data clearing or missed entries.",
  },
  {
    value: "NEGATIVE_VALUE_RULE",
    label: "Negative Value",
    info: "Checks whether any submitted value is below zero. Useful for metrics like energy, emissions, and waste that should not be negative.",
  },
  {
    value: "CONSECUTIVE_IDENTICAL_RULE",
    label: "Same Value Repeated",
    info: "Checks whether the same non-zero value is entered for multiple periods in a row. Helps spot copy-paste or stale data entry.",
  },
  {
    value: "MISSING_PERIOD_GAP_RULE",
    label: "Missing Period",
    info: "Checks whether any expected reporting period is missing from the data history. Helps identify skipped or late submissions.",
  },
  {
    value: "UOM_MID_PERIOD_CHANGE_RULE",
    label: "UOM Changed",
    info: "Checks whether the unit of measure changed from the previous period, which can affect trend comparisons.",
  },
  {
    value: "APPLICABLE_DATAPOINT_RULE",
    label: "Missing Required Data",
    info: "Checks whether all required applicable data points have been submitted for each branch in the current period.",
  },
];

export const RULE_CODE = {
  SPIKE:                  "SPIKE_RULE",
  DROP:                   "DROP_RULE",
  ZERO_AFTER_NON_ZERO:    "ZERO_AFTER_NON_ZERO_RULE",
  MISSING_PERIOD_GAP:     "MISSING_PERIOD_GAP_RULE",
  NEGATIVE_VALUE:         "NEGATIVE_VALUE_RULE",
  CONSECUTIVE_IDENTICAL:  "CONSECUTIVE_IDENTICAL_RULE",
  UOM_MID_PERIOD_CHANGE:  "UOM_MID_PERIOD_CHANGE_RULE",
  APPLICABLE_DATAPOINT:   "APPLICABLE_DATAPOINT_RULE",
};

/** Rules where the API populates expectedValue / baseline (prior period or zero). */
export const RULES_WITH_EXPECTED_VALUE = [
  RULE_CODE.SPIKE,
  RULE_CODE.DROP,
  RULE_CODE.NEGATIVE_VALUE,
];

/** Detail modal: show expected value / deviation only for these rules. */
export const ruleCodeShowsExpectedValue = (ruleCode) =>
  Boolean(ruleCode) && RULES_WITH_EXPECTED_VALUE.includes(ruleCode);

/**
 * Timeline: allow baseline when rule filter is All (empty) or one of the
 * expected-value rules. Blocks ghost line / compare when a non-baseline rule
 * is selected.
 */
export const shouldShowTimelineExpectedBaseline = (activeRuleCode) =>
  !activeRuleCode || RULES_WITH_EXPECTED_VALUE.includes(activeRuleCode);

/** Per data point: global baseline allowed and API sent expectedValue. */
export const dataPointShowsExpectedBaseline = (activeRuleCode, dataPoint) =>
  shouldShowTimelineExpectedBaseline(activeRuleCode)
  && dataPoint?.expectedValue != null;

export const getAnomalyRuleLabelKey = (ruleCode) =>
  `AnomalyRuleConfig.${ruleCode}.label`;

export const getAnomalyRuleDescriptionKey = (ruleCode) =>
  `AnomalyRuleConfig.${ruleCode}.description.label`;

export const getAnomalyRuleLabel = (ruleCode) =>
  ANOMALY_RULE_CODES.find((r) => r.value === ruleCode)?.label || ruleCode || "—";

export const getAnomalyRuleInfo = (ruleCode) =>
  ANOMALY_RULE_CODES.find((r) => r.value === ruleCode)?.info || "";

export const resolveAnomalyRuleLabel = (t, ruleCode) =>
  t(getAnomalyRuleLabelKey(ruleCode), { defaultValue: getAnomalyRuleLabel(ruleCode) });

export default ANOMALY_RULE_CODES;
